import { Gapped, Input, Select } from "react-ui";
import { ComboBox } from "@skbkontur/react-ui/components/ComboBox";
import * as React from "react";
import { useEffect, useState } from "react";

import { Root, Row, Caption, Value } from "../FiasFnsManualEditor.styles";
import { AddressLevel, FiasAddressType } from "../../../FiasInput/Types/FiasTypes";
import { getAddBuildingItems } from "../FiasFnsManualEditor/FiasFnsManualEditorUtils";
import { dirtyReduxHack } from "../../../Utils/FiasAddressUtils";
import { getHouseTypeLongName } from "../../../FiasInput/Utils/Convertations/House/House";
import { konturFiasAddressAbbreviations } from "../../../FiasInput/Utils/FiasAbbriviations/FilteredAbbreviations";
import { getFullAddressByFullName } from "../../../FiasInput/Utils/FiasAbbriviations/FiasAbbreviations";
import { FIAS_HANDLE_EDIT_MODE_VALUE_CHANGE } from "../../../../../metricsAction";
import { useMetrics } from "../../../../../Internals/Metrics/MetricsProviderContext";
import { useTrackFiasModalEnter } from "../../../FiasMetrics";

import { FiasMvdAddress, getMvdBuildingItems } from "./FiasMvdManualEditorUtils";

interface FiasMvdManualEditorProps {
    mvdAddress: FiasMvdAddress;
    setMvdAddress: (nextValue: FiasMvdAddress) => void;
}

export function FiasMvdManualEditor({ mvdAddress, setMvdAddress }: FiasMvdManualEditorProps): JSX.Element {
    useTrackFiasModalEnter("mvd");

    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [metricSent, setMetricSent] = useState<boolean>(false);
    const track = useMetrics();

    useEffect(() => {
        if (isChanged && !metricSent) {
            track(FIAS_HANDLE_EDIT_MODE_VALUE_CHANGE);
            setMetricSent(true);
        }
        setIsChanged(true);
    }, dirtyReduxHack(mvdAddress));

    const buildingItems = getMvdBuildingItems(mvdAddress.House?.Type);
    const add1BuildingItems = getAddBuildingItems(mvdAddress.Housing?.Type);
    const add2BuildingItems = getAddBuildingItems(mvdAddress.Housing?.Type);
    return (
        <Root>
            <Row>
                <Caption>Регион</Caption>
                <Value>
                    <Input
                        placeholder={"Наименование"}
                        width={350}
                        value={mvdAddress.Region ?? ""}
                        onValueChange={nextValue => setMvdAddress({ ...mvdAddress, Region: nextValue })}
                    />
                </Value>
            </Row>
            <Row>
                <Caption>Район</Caption>
                <Value>
                    <Input
                        placeholder={"Наименование"}
                        width={350}
                        value={mvdAddress.District ?? ""}
                        onValueChange={nextValue => setMvdAddress({ ...mvdAddress, District: nextValue })}
                    />
                </Value>
            </Row>
            <Row>
                <Caption>Город</Caption>
                <Value>
                    <Input
                        width={350}
                        placeholder={"Наименование"}
                        value={mvdAddress.City ?? ""}
                        onValueChange={nextValue => setMvdAddress({ ...mvdAddress, City: nextValue })}
                    />
                </Value>
            </Row>
            <Row>
                <Caption>Населенный пункт</Caption>
                <Value>
                    <Input
                        width={350}
                        value={mvdAddress.Settlement ?? ""}
                        placeholder={"Наименование"}
                        onValueChange={nextValue => setMvdAddress({ ...mvdAddress, Settlement: nextValue })}
                    />
                </Value>
            </Row>
            <Row>
                <Caption>Внутригородская территория</Caption>
                <Value>
                    <Input
                        width={350}
                        value={mvdAddress.PlanningStructure ?? ""}
                        placeholder={"Наименование"}
                        onValueChange={nextValue => setMvdAddress({ ...mvdAddress, PlanningStructure: nextValue })}
                    />
                </Value>
            </Row>
            <Row>
                <Caption>Улица</Caption>
                <Value>
                    <Input
                        width={350}
                        maxLength={255}
                        value={mvdAddress.Street ?? ""}
                        placeholder={"Наименование"}
                        onValueChange={nextValue => setMvdAddress({ ...mvdAddress, Street: nextValue })}
                    />
                </Value>
            </Row>
            <Row>
                <Caption>Номер здания / сооружения</Caption>
                <Gapped>
                    <Select<string | undefined, string>
                        width={80}
                        value={getHouseTypeLongName(mvdAddress.House?.Type)}
                        onValueChange={nextValue =>
                            setMvdAddress({
                                ...mvdAddress,
                                House: { Type: nextValue, Number: mvdAddress.House?.Number },
                            })
                        }
                        placeholder={"Тип"}
                        data-tid={"BuildingType"}
                        items={buildingItems}
                    />

                    <Input
                        width={80}
                        maxLength={255}
                        value={mvdAddress.House?.Number ?? ""}
                        placeholder={"Номер"}
                        data-tid={"HouseNum"}
                        onValueChange={nextValue =>
                            setMvdAddress({ ...mvdAddress, House: { Type: mvdAddress.House?.Type, Number: nextValue } })
                        }
                    />
                </Gapped>
            </Row>
            <Row>
                <Caption />
                <Gapped>
                    <Select<string | undefined, string>
                        width={80}
                        value={getHouseTypeLongName(mvdAddress.Housing?.Type)}
                        onValueChange={nextValue =>
                            setMvdAddress({
                                ...mvdAddress,
                                Housing: { Type: nextValue, Number: mvdAddress.Housing?.Number },
                            })
                        }
                        placeholder={"Тип"}
                        data-tid={"BuildingType"}
                        items={add1BuildingItems}
                    />
                    <Input
                        width={80}
                        maxLength={255}
                        value={mvdAddress.Housing?.Number ?? ""}
                        placeholder={"Номер"}
                        data-tid={"BuildingNum"}
                        onValueChange={nextValue =>
                            setMvdAddress({
                                ...mvdAddress,
                                Housing: { Type: mvdAddress.Housing?.Type, Number: nextValue },
                            })
                        }
                    />
                </Gapped>
            </Row>
            <Row>
                <Caption />
                <Gapped>
                    <Select<string | undefined, string>
                        width={80}
                        value={getHouseTypeLongName(mvdAddress.Structure?.Type)}
                        onValueChange={nextValue =>
                            setMvdAddress({
                                ...mvdAddress,
                                Structure: { Type: nextValue, Number: mvdAddress.Structure?.Number },
                            })
                        }
                        placeholder={"Тип"}
                        data-tid={"BuildingType"}
                        items={add2BuildingItems}
                    />
                    <Input
                        width={80}
                        maxLength={255}
                        value={mvdAddress.Structure?.Number ?? ""}
                        placeholder={"Номер"}
                        data-tid={"BuildingNum"}
                        onValueChange={nextValue =>
                            setMvdAddress({
                                ...mvdAddress,
                                Structure: { Type: mvdAddress.Structure?.Type, Number: nextValue },
                            })
                        }
                    />
                </Gapped>
            </Row>
            <Row>
                <Caption>Номер помещения / квартиры / машино-места</Caption>
                <Gapped>
                    <ComboBox<FiasAddressType>
                        width={80}
                        getItems={async query =>
                            konturFiasAddressAbbreviations
                                .filter(
                                    x =>
                                        x.level === AddressLevel.Room &&
                                        x.fullName.toLowerCase().includes(query.toLowerCase())
                                )
                                .slice(0, 10)
                        }
                        placeholder={"Тип"}
                        data-tid={"ApartmentType"}
                        value={
                            getFullAddressByFullName(mvdAddress.Flat?.Type) || {
                                fullName: mvdAddress.Flat?.Type,
                            }
                        }
                        renderItem={item => `${item.fullName} - ${item.shortName}`}
                        renderValue={item => item.fullName}
                        valueToString={item => item.fullName}
                        onValueChange={x =>
                            setMvdAddress({
                                ...mvdAddress,
                                Flat: { Type: x.fullName, Number: mvdAddress.Flat?.Number },
                            })
                        }
                        onUnexpectedInput={x =>
                            setMvdAddress({
                                ...mvdAddress,
                                Flat: {
                                    Type: typeof x === "string" ? x : "",
                                    Number: mvdAddress.Flat?.Number,
                                },
                            })
                        }
                        drawArrow={false}
                        maxLength={50}
                    />
                    <Input
                        width={260}
                        maxLength={255}
                        value={mvdAddress.Flat?.Number ?? ""}
                        placeholder={"Номер"}
                        data-tid={"ApartmentNum"}
                        onValueChange={nextValue =>
                            setMvdAddress({
                                ...mvdAddress,
                                Flat: { Type: mvdAddress.Flat?.Type, Number: nextValue },
                            })
                        }
                    />
                </Gapped>
            </Row>
        </Root>
    );
}
